import { ContractData, Data } from "./types";
import {
  endOfDay,
  startOfDay,
  toDate,
  isSameDay,
  addBusinessDays,
  compareAsc,
  compareDesc,
  isWeekend,
  Interval,
  areIntervalsOverlapping,
  differenceInCalendarDays,
  differenceInBusinessDays,
  isBefore,
  isAfter,
} from "date-fns";

const getIntervalBetweenDateAndFirstNextBusinessDay = (date: Date, holidays: Date[]) => {
  const interval: Interval = {
    start: startOfDay(date),
    end: date,
  };

  //If the end day is in the weekend, go to the next business day
  if (isWeekend(interval.end)) {
    interval.end = addBusinessDays(interval.start, 1);
  }

  interval.end = endOfDay(interval.end);

  //For the end date, check if the date is a holiday and shift by one.
  for (const holiday of holidays.sort(compareAsc)) {
    if (isSameDay(interval.end, holiday)) {
      interval.end = addBusinessDays(interval.end, 1);
    }
  }

  interval.end = endOfDay(interval.end);

  return interval;
};

const getIntervalBetweenDateAndFirstPreviousBusinessDay = (date: Date, holidays: Date[]) => {
  const interval: Interval = {
    start: date,
    end: endOfDay(date),
  };

  //If the start date is in the weekend, go to the previous business day
  if (isWeekend(interval.start)) {
    interval.start = addBusinessDays(interval.start, -1);
  }
  interval.start = startOfDay(interval.start);

  //For the start date, check if the date is a holiday and shift one back.
  for (const holiday of holidays.sort(compareDesc)) {
    if (isSameDay(interval.start, holiday)) {
      interval.start = addBusinessDays(interval.start, -1);
    }
  }

  interval.start = startOfDay(interval.start);

  return interval;
};

const isActive = (data: Data, holidays: Date[]) => {
  // additional logic from email Annelies on 20/04/2021
  // De voorwaarden van het contract op de eerste en de laatste dag is een contract van minstens 50% OR 60%
  // Enkel een contract is onvoldoende.

  const quarterStartInterval = getIntervalBetweenDateAndFirstNextBusinessDay(
    toDate(data.quarter.start),
    holidays
  );
  const quarterEndInterval = getIntervalBetweenDateAndFirstPreviousBusinessDay(
    toDate(data.quarter.end),
    holidays
  );

  //Loop over the contracts and check if it overlaps with the quarter start intervals.
  const contractQuarterStartOverlaps = data.contractDataArray.map((contractData) => {
    return (
      areIntervalsOverlapping(quarterStartInterval, contractData.interval, {
        inclusive: true,
      }) &&
      // this condition also checks if employmentRate > 50% OR 60%
      contractData.employmentRateNumerator / contractData.employmentRateDenominator >=
        data.contractEmploymentRateCondition
    );
  });

  //Loop over the contracts and check if it overlaps with the quarter end intervals.
  const contractQuarterEndOverlaps = data.contractDataArray.map((contractData) => {
    return (
      areIntervalsOverlapping(quarterEndInterval, contractData.interval, { inclusive: true }) &&
      // this condition also checks if employmentRate > 50% OR 60%
      contractData.employmentRateNumerator / contractData.employmentRateDenominator >=
        data.contractEmploymentRateCondition
    );
  });

  const isBeginActive = data.zibInPreviousQuarter || contractQuarterStartOverlaps.some((e) => e);
  const isEndActive = contractQuarterEndOverlaps.some((e) => e);
  const isActive = isBeginActive && isEndActive;

  console.log({
    isBeginActive: isBeginActive,
    isEndActive: isEndActive,
    isActive: isActive,
  });

  return isActive;
};

const getNumberOfWorkingDays = (contract: ContractData, quarter: Interval) => {
  // here we should only consider the days within the quarter.
  // therefore we if contract.interval.start is before quarter.start => use quarter.start
  // if contract.inteval.end is after quarter.end => use quarter.end
  const start = isBefore(contract.interval.start, quarter.start)
    ? quarter.start
    : contract.interval.start;
  const end = isAfter(contract.interval.end, quarter.end) ? quarter.end : contract.interval.end;

  const numberOfCalendarDays = differenceInCalendarDays(end, start);
  let numberOfWeekDays: number = 0;
  let numberOfWorkingDays: number = 0;

  if (numberOfCalendarDays < 8) {
    numberOfWorkingDays = numberOfCalendarDays;
  } else {
    numberOfWeekDays = differenceInBusinessDays(end, start);
    numberOfWorkingDays = numberOfWeekDays;
  }

  return numberOfWorkingDays;
};

const getContractEmploymentRate = (contract: ContractData, quarter: Interval) => {
  const numberOfWorkingDays = getNumberOfWorkingDays(contract, quarter);
  return (
    (numberOfWorkingDays * contract.employmentRateNumerator) /
    contract.employmentRateDenominator /
    62
  );
};

const getContractPerformedHours = (contract: ContractData, quarter: Interval) => {
  let performedHours = contract.performedHours;

  if (performedHours == null) {
    const numberOfWorkingDays = getNumberOfWorkingDays(contract, quarter);
    performedHours = (numberOfWorkingDays * contract.employmentRateNumerator) / 5;
  }

  return (performedHours / contract.employmentRateDenominator) * 38;
};

const sum = (accumulator: number, currentValue: number) => accumulator + currentValue;

const getEmploymentRate = (data: Data) => {
  return data.contractDataArray
    .map((contract) => {
      return getContractEmploymentRate(contract, data.quarter);
    })
    .reduce(sum);
};

const getPerformedHours = (data: Data) => {
  return data.contractDataArray
    .map((contract) => {
      return getContractPerformedHours(contract, data.quarter);
    })
    .reduce(sum);
};

export {
  getIntervalBetweenDateAndFirstPreviousBusinessDay as getCalendarAndBusinessDayEndInterval,
  getIntervalBetweenDateAndFirstNextBusinessDay as getCalendarAndBusinessDayStartInterval,
  isActive,
  getNumberOfWorkingDays,
  getEmploymentRate,
  getPerformedHours,
};
